// Sealed-bid tender seal/unseal section (migration 020) — shared between
// the admin approval decision screen (pages/ApprovalDetailPage.jsx) and the
// "Quotations" read-only document view (pages/AllProformasViewPage.jsx).
// Moved out of ApprovalDetailPage (this task's brief: "the seal section
// must be reachable for a quotation in ANY state, not only pending
// approval") — ApprovalDetailPage's own read (getForApproval) never filtered
// by status, but nothing linked to it once a quotation left
// 'pending_approval' (it drops off the approval queue and nothing else
// pointed at /quotations/:id), so in practice the section was only ever
// reachable by typing that URL directly. AllProformasViewPage IS linked
// from a list that covers every quotation in every state
// (pages/AllProformasPage.jsx), so it's the nav-reachable home for this
// section going forward; ApprovalDetailPage keeps rendering it too (a
// tender already sealed while still pending approval is a real, valid
// state worth showing on the decision screen itself).
//
// The unseal MUTATION is injected by the caller rather than created here,
// because the two callers cache this quotation under two different
// react-query keys (['admin','approvalDetail',id] vs
// ['admin','proformaDocument',id] — see hooks/useApprovalDetail.js's own
// useUnsealQuotation and hooks/useAllProformas.js's own
// useUnsealProformaDocument) — a single hardcoded hook here would silently
// update the wrong cache, the exact class of stale-cache bug already found
// and fixed once in this codebase (see PROGRESS.md's sealed-bid-tender
// entry). `unsealMutation` must be a TanStack `useMutation` result whose
// `mutate()` takes no argument and whose success response is
// `{ quotation }`.
//
// `quotation` fields this component reads: id (to filter the access-log
// entries below), sealed_at, seal_bid_opening_at, unsealed_at,
// unsealed_by_name, unseal_route. i18n keys keep their historical
// `approvalDetail.seal.*` prefix rather than a generic `seal.*` one — pure
// continuity (every string already exists, proven correct in both
// languages); the prefix name no longer implies "only on the approval
// detail page".

import { useDocumentAccessLog } from '../hooks/useDocumentAccessLog.js';
import { useActionFeedback } from '../hooks/useActionFeedback.js';
import { Spinner } from 'shared/components/Feedback/index.js';
import { formatDateTime } from '../utils/formatDate.js';
import styles from './SealSection.module.css';

export default function SealSection({ quotation, requestId, unsealMutation }) {
  const { t, lang, confirm, toastOk, toastErr } = useActionFeedback();
  const { data: accessEntries } = useDocumentAccessLog(requestId);

  const openEntries = (accessEntries || []).filter(
    (entry) =>
      (entry.document_type === 'quotation_pdf' || entry.document_type === 'quotation_view') &&
      entry.quotation_id === quotation.id
  );
  const firstOpenedEntry = openEntries.length > 0 ? openEntries[openEntries.length - 1] : null;

  const handleUnseal = async () => {
    const ok = await confirm({
      title: t('approvalDetail.seal.confirmUnsealTitle'),
      message: t('approvalDetail.seal.confirmUnsealBody'),
      confirmLabel: t('approvalDetail.seal.unsealNow'),
      tone: 'danger',
    });
    if (!ok) return;

    unsealMutation.mutate(undefined, {
      onSuccess: () => toastOk(t('approvalDetail.seal.unsealedToast')),
      onError: (err) =>
        toastErr(err, [
          'QUOTATION_NOT_FOUND',
          'QUOTATION_NOT_SEALED',
          'QUOTATION_ALREADY_UNSEALED',
          'UNSEAL_NOT_PERMITTED',
        ]),
    });
  };

  const unsealedByLabel = quotation.unsealed_by_name
    ? quotation.unsealed_by_name
    : quotation.unseal_route === 'scheduled'
      ? t('approvalDetail.seal.systemScheduled')
      : t('approvalDetail.seal.unknownActor');

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>{t('approvalDetail.seal.title')}</h2>
      <p className={styles.meta}>
        <strong>{t('approvalDetail.seal.status')}:</strong>{' '}
        {quotation.unsealed_at
          ? t('approvalDetail.seal.statusUnsealed')
          : t('approvalDetail.seal.statusSealed')}
      </p>
      <p className={styles.meta}>
        <strong>{t('approvalDetail.seal.sealedAt')}:</strong>{' '}
        <span className="tabularNums">{formatDateTime(quotation.sealed_at, lang) || '—'}</span>
      </p>
      {quotation.seal_bid_opening_at && (
        <p className={styles.meta}>
          <strong>{t('approvalDetail.seal.bidOpeningAt')}:</strong>{' '}
          <span className="tabularNums">
            {formatDateTime(quotation.seal_bid_opening_at, lang) || '—'}
          </span>
        </p>
      )}
      <p className={styles.meta}>
        <strong>{t('approvalDetail.seal.firstOpenedAt')}:</strong>{' '}
        {firstOpenedEntry ? (
          <span className="tabularNums">
            {formatDateTime(firstOpenedEntry.accessed_at, lang) || '—'}
          </span>
        ) : (
          t('approvalDetail.seal.firstOpenedNone')
        )}
      </p>

      {quotation.unsealed_at ? (
        <>
          <p className={styles.meta}>
            <strong>{t('approvalDetail.seal.unsealedAt')}:</strong>{' '}
            <span className="tabularNums">
              {formatDateTime(quotation.unsealed_at, lang) || '—'}
            </span>
          </p>
          <p className={styles.meta}>
            <strong>{t('approvalDetail.seal.unsealedBy')}:</strong> {unsealedByLabel}
          </p>
          <p className={styles.meta}>
            <strong>{t('approvalDetail.seal.unsealRoute')}:</strong>{' '}
            {t(`approvalDetail.seal.route.${quotation.unseal_route || 'manual'}`)}
          </p>
        </>
      ) : quotation.sealed_at ? (
        <button
          type="button"
          className={styles.actionButton}
          onClick={handleUnseal}
          disabled={unsealMutation.isPending}
        >
          {unsealMutation.isPending && <Spinner />}{' '}
          {unsealMutation.isPending
            ? t('approvalDetail.seal.unsealing')
            : t('approvalDetail.seal.unsealNow')}
        </button>
      ) : (
        // sealed_at is only stamped once the quotation is actually sent
        // (services/quotationService.js#markSent) — while still pending
        // approval there is nothing to unseal yet; the server would reject
        // an attempt with QUOTATION_NOT_SEALED. No button here, matching
        // that.
        <p className={styles.muted}>{t('approvalDetail.seal.pendingSendNote')}</p>
      )}
    </section>
  );
}
